/**
 * The indicator catalog — what a chart can draw, and where each one goes.
 *
 * Follows `compile-expr.ts`'s rules, which are the app's rules for talking to a
 * service: the base URL is read once at module load, a missing one makes the
 * feature *degrade* rather than error, and nothing here throws. An empty
 * catalog is a legitimate answer — the picker then says it has nothing to
 * offer, and the rest of the app runs exactly as it does with no `VITE_QV_SET_URL`.
 *
 * `/ind/catalog` is served by qv-data, the same app as `/set/*` and `/sym/*`, so
 * `VITE_QV_IND_URL` falls back to `VITE_QV_SET_URL` for the same reason
 * `symbols.ts` does: one service, one origin, and nobody has to set two
 * variables to the same value.
 *
 * **The catalog is the source of placement.** `placement` is a property of the
 * indicator, not a preference — RSI on the price axis is a flat line at the
 * bottom of the pane — so the server declares it and `lib/add-indicator.ts`
 * places the plot without deciding anything. Likewise `stroke`: a *token*, not a
 * colour, resolved by the theme at render (D13).
 */

import type { AxisLevel, PlotStyle, PlotType } from "@qufolio/model";
import { onActingAsChanged } from "../lib/act-as";
import { onAuthChanged } from "../lib/session";
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const IND_URL = httpBase(
  import.meta.env.VITE_QV_IND_URL,
  import.meta.env.VITE_QV_SET_URL,
);

/**
 * One editable parameter, with the value used when nobody touches it.
 *
 * `kind` decides the control. Every parameter was a number until Symbol
 * Indicators arrived, so `number` is the default and the field is optional — an
 * older server that does not send it still describes a spinner.
 */
export interface IndicatorParamSpec {
  name: string;
  default: number | string;
  label: string;
  /**
   * `bool` is a checkbox whose value still travels as **1 or 0**, because its
   * destination is an alias and `parse_alias` reads only numbers. The DSL spells
   * the same argument `true`; the server's `_Lowering.flag` takes either.
   */
  kind?: "number" | "symbol" | "choice" | "bool";
  /** The options a `choice` offers, in the order shown. */
  choices?: string[];
}

/**
 * One drawable series of a family. `name` is the head of the alias the client
 * asks for it by (`macdSignal` → `macdSignal_12_26_9`).
 */
export interface IndicatorOutputSpec {
  name: string;
  label: string;
  /**
   * The renderable this output draws as.
   *
   * `volumeByPrice` is the one value that changes what an entry *means*: that
   * plot has no column behind it. Its bins are computed on the client from the
   * bars the candles already loaded, so the family declares an output the server
   * never answers, and `chart-feed`'s `plotFeeds` skips the type rather than
   * requesting it. Said here because this interface is where a reader looks when
   * an alias goes unanswered, and every other output on it is a column.
   */
  plot: PlotType;
  /** A theme token (`series1`…`series6`) — never a hex. */
  stroke: string;
  /**
   * Per-bar colouring, when the shape carries it. `sign` colours by the value's
   * own sign — the only reading a histogram around zero has — and `delta` by the
   * change from the previous bar.
   *
   * **Absent means one colour**, deliberately, rather than a null: a null would
   * make every client decide what null means, and one of them would get it wrong.
   */
  colorMode?: "sign" | "delta" | "quad";
  /**
   * `PlotStyle.dashStyle`. A signal line beside the line it smooths wants to be
   * visibly secondary; absent means solid.
   */
  dash?: "solid" | "dashed" | "dotted";
  /**
   * `PlotStyle.digital` — draw as a step rather than interpolating.
   *
   * For a series that holds a value until it changes. A pivot level is last
   * month's number forward-filled across this month, so a straight line between
   * two of them invents a slope neither ever had.
   */
  digital?: boolean;
  /**
   * `PlotStyle.markerShape` for a signal or scatter output whose glyph the
   * indicator decides — PPS's ▲ buy and ▼ sell. Absent is the renderer's
   * default, circle.
   */
  markerShape?: NonNullable<PlotStyle["markerShape"]>;
  /** `PlotStyle.markerSize` in px, where the default size is part of the reading. */
  markerSize?: number;
  /**
   * `PlotStyle.thickness` when the indicator decides the width — the Rank
   * signal lines are the thinnest (owner, 2026-10-30). Absent is the default
   * for the plot type.
   */
  thickness?: number;
  /** `PlotStyle.markerText`: what a `text` marker reads by default — FTD's `FTD` / `DD`. */
  markerText?: string;
  /**
   * `PlotSignal.row` for a signal output that shares a lane with its siblings
   * — DOTS' Squeeze On and Squeeze Fired. Absent is a lane of its own.
   */
  signalRow?: string;
  /**
   * `PlotSignal.level` — the height a signal output's markers sit at, 1 being
   * the bottom row, for a family whose rows are spaced on purpose (JAS
   * Pressure, owner 2026-10-03). Absent is the packed layout.
   */
  signalLevel?: number;
  /**
   * How a signal output's values read — the indicator's meaning, so it is
   * **looked up here at render time and never stored on the plot**. Absent is
   * `single`: drawn only where non-zero, 0 blank. `dual` draws 1 in the up
   * colour and 0 in the down colour, blank only where there is no value —
   * JAS Timing. See `cachedEventKinds`.
   */
  eventKind?: "single" | "dual" | "flag";
}

/** One reference line on an indicator's own axis — RSI's 30, MACD's zero. */
export interface IndicatorLevelSpec {
  value: number;
  label?: string;
  dash?: "solid" | "dashed" | "dotted";
  /** Shade the line past this level — RSI's 70 above, 30 below (owner, 2026-11-02). */
  fill?: AxisLevel["fill"];
}

/**
 * What an indicator's own Y axis should be, before the user touches it.
 *
 * A property of the **family**, not of an output — which is why nothing carried
 * it until now, and why every oscillator ever added inherited a hardcoded
 * `autoRange: "always"` and `format: "0.00"`.
 *
 * **`min`/`max` and `levels` are independent.** CCI is the case that proves it:
 * every reference chart draws its ±100 lines, which reads as though the axis is
 * bounded, and it is not — CCI runs well past ±300 and pinning it would clip the
 * reading exactly when that matters. So "levels without a range" is a legitimate
 * combination and a single `bounded` flag would have been wrong.
 *
 * **The catalog's levels are drawn, never stored.** They are synthesised into
 * the render plan each build rather than written into `Pane.annotations`, so a
 * convention corrected server-side reaches every existing chart and no workspace
 * grows rows for them (D8). A level the *user* places is the opposite case —
 * nothing derives it — so it is stored on `AxisInfo.levels`, and an axis
 * carrying that array answers for the whole of what is drawn on it: these
 * defaults and the `zeroLine` below are then not consulted at all.
 */
export interface IndicatorAxisSpec {
  min?: number;
  max?: number;
  /** Meaningful for an oscillator that straddles zero, meaningless for one whose
   *  floor is zero — which is why it is declared rather than inferred. */
  zeroLine: boolean;
  levels?: IndicatorLevelSpec[];
  /** `AxisInfo.format`. */
  format: string;
  /**
   * `AxisInfo.scale` — absent means linear, which is every family but one.
   *
   * A property of what the number *is*, like `min`/`max` above it. Band width
   * spans roughly 2% to 40%, so a linear axis spends most of its height on the
   * calm half and compresses the move that matters into the top inch; ranked
   * against its own history it is a percentile, and a percentile reads off a log
   * axis and not off a linear one.
   *
   * Optional and additive: a server that sends none — an older one, or any of
   * the other 158 entries — lands exactly on the linear default.
   */
  scale?: "linear" | "logarithmic" | "signedLog";
}

/**
 * The catalog's own lines, in the shape `AxisInfo.levels` stores.
 *
 * Two jobs, and they have to be the same array or the feature is dishonest: it
 * is what the settings panel lists while an axis stores nothing, and it is what
 * the panel *seeds* the stored array with on the first edit. Switching from the
 * catalog's levels to your own must never move a line on screen, and one
 * function producing both is what makes that true by construction rather than by
 * two lists agreeing.
 *
 * So it has to reproduce what `pane-plan.ts::referenceLines` draws from the same
 * spec, down to the dash: the zero line is emitted separately there and carries
 * no dash, and a catalog level's `dash` is passed through untouched — `plotDashFor`
 * defaults an absent one to solid. Defaulting anything here would seed a set that
 * draws differently from the set it replaced, which is the one outcome this is
 * for. (The server spells every level `dotted` today; that is its choice to
 * change, not ours to bake in.)
 *
 * Sorted **down the axis**, high value first, which is the order both the
 * inherited list and the seeded one are read in. The array's order carries no
 * meaning to the renderer, so this is free — and it is one ordering rather than
 * two precisely so that the list does not visibly reshuffle itself the moment
 * somebody clicks Custom.
 */
export function catalogLevels(axis: IndicatorAxisSpec | undefined): AxisLevel[] {
  if (!axis) return [];
  // Zero first only until the sort runs; `zeroLine` is a flag rather than an
  // entry in `levels` because an axis whose floor is zero has no use for it, so
  // it cannot simply be read out of the list.
  const lines: AxisLevel[] = axis.zeroLine ? [{ value: 0 }] : [];
  for (const level of axis.levels ?? []) {
    lines.push({
      value: level.value,
      ...(level.label !== undefined ? { label: level.label } : {}),
      ...(level.dash !== undefined ? { dash: level.dash } : {}),
      // Carried, or clicking Custom would drop RSI's fills from the screen.
      ...(level.fill !== undefined ? { fill: { ...level.fill } } : {}),
    });
  }
  return lines.sort((a, b) => b.value - a.value);
}

/**
 * Two series that also draw as a filled band beneath their own lines.
 *
 * An *extra* plot rather than a replacement for the two edges, so upper and
 * lower keep their separate styling and their own hide toggles. It is the one
 * thing that needed the contract to move — a `band` renderable wants two y
 * columns per point, so `Plot.outputAlias2` names the second.
 *
 * **An edge may name a base column rather than an output**, which is what a fill
 * between price and an indicator is. `close` is in every price frame already, so
 * it is neither templated with the family's parameters nor requested from the
 * server — `toIndicatorRequest` skips `BASE_COLUMNS` and `bulkDeltas` reads the
 * column it is already holding.
 */
export interface IndicatorBandSpec {
  upper: string;
  lower: string;
  opacity: number;
  /**
   * The theme token the fill takes. Absent falls back to the edge's own stroke,
   * which is what a channel wants — neither Bollinger edge leads, so one wash
   * off the family's colour is the whole reading. A band whose colour *is* the
   * reading names it.
   */
  fill?: string;
  /**
   * Fill in **two** colours, flipping where the edges cross.
   *
   * A channel takes one wash because neither edge leads. Ichimoku's cloud is the
   * opposite — which Senkou is on top *is* the reading — so it asks for the
   * paired palette `addBandBetween` already mints for a user-composed band.
   */
  paired?: boolean;
  /**
   * What the style editor calls this row.
   *
   * A band shares its primary alias with the line it is drawn under, and the
   * picker labels a plot by looking its alias up among the family's outputs — so
   * without this a fill and its edge are two rows both reading "Long stop".
   */
  label?: string;
}

export interface IndicatorEntry {
  name: string;
  label: string;
  category: string;
  /** `price` draws over the candles; `own` gets a pane of its own. */
  placement: "price" | "own";
  description: string;
  /**
   * Whether this family can be continued past the last bar, over projected
   * closes. Declared by the server for `placement`'s reason: it follows from
   * what the indicator *reads* — volume cannot be projected, and a handful
   * measure the very range a projection has to assume — and no client can work
   * that out for itself.
   */
  projectable?: boolean;
  /**
   * Whether this family can be drawn weekly or monthly on a daily chart
   * (`higherTimeframe` from `/ind/catalog`; `qv-data/app/qf/higher_tf.py`).
   */
  higherTimeframe?: boolean;
  /**
   * Whether this account's plan includes the category this family is in.
   *
   * **Marked rather than omitted**, and that is a product decision: the picker
   * is where somebody finds out a feature exists, and a list that silently drops
   * everything they cannot draw teaches that it was never there. The server
   * sets it (`/ind/catalog`) and refuses the alias separately — this is what
   * the row renders, not what decides.
   */
  locked?: boolean;
  params: IndicatorParamSpec[];
  /** In declared order — a family lands together, in one pane. */
  outputs: IndicatorOutputSpec[];
  /**
   * How this entry spells an alias, `{param}` substituted — `{field}@{symbol}`.
   *
   * Absent means the ordinary `<output>_<p1>_<p2>` form. It comes from the
   * server because the server is what parses the alias back: two spellings kept
   * in two places drift into a plot that is requested and never answered.
   */
  aliasTemplate?: string;
  /**
   * The pane's own Y axis, for a `placement: "own"` family. Absent means the
   * family shares the price axis, or that the server predates the field — in
   * which case the axis auto-ranges, exactly as it did before.
   */
  axis?: IndicatorAxisSpec;
  /**
   * The fills this family draws under its lines. Absent for most of them.
   *
   * A list because a channel has one band — between its own two edges — and
   * Chandelier has two, price against each of its stops, only one of which is
   * live on any bar. A single band would have to change which series it filled
   * against halfway along the chart.
   */
  bands?: IndicatorBandSpec[];
  /**
   * A line and its own average, by output name — the chart reads the gap
   * between them (`lib/gaps.ts`). JAS OBV with EMA and RS to SPY.
   */
  gap?: { line: string; average: string };
}

/**
 * Every indicator a chart can draw, in the server's own order — which is
 * meaningful (Overlap Studies, Momentum, Volatility, Volume, Statistic) and is
 * what the picker groups by, so it is never re-sorted here.
 *
 * `[]` for an unreachable or unconfigured service, never a rejection: the
 * button that opens the picker is the only caller and a thrown promise there
 * would be an unhandled rejection behind a dialog that simply failed to fill.
 */
/**
 * The catalog, fetched once for the page.
 *
 * Module scope for the reason `symbol-index.ts` is: it is a fact about the
 * page, not about a component, and two holders of it are two that can disagree.
 * It lived in `IndicatorPicker` while the picker was its only reader — the chart
 * now needs it too, for the reference lines an indicator declares, and a second
 * fetch would be a second answer.
 *
 * An empty result clears the memo rather than caching it, so a server that was
 * down when the page loaded is retried on the next ask instead of leaving the
 * app permanently catalog-less.
 */
let catalogPromise: Promise<IndicatorEntry[]> | null = null;
let catalogCache: IndicatorEntry[] = [];

// **The catalog is per caller** — a group's own families are listed only to its
// members — so who-we-are changing makes the memo wrong. Asked again on the
// next read, not cleared, so the picker never flashes empty.
const forgetCatalog = (): void => {
  catalogPromise = null;
};
onAuthChanged(forgetCatalog);
onActingAsChanged(forgetCatalog);

export function loadIndicatorCatalog(): Promise<IndicatorEntry[]> {
  if (!catalogPromise) {
    catalogPromise = fetchIndicatorCatalog().then((rows) => {
      if (rows.length === 0) catalogPromise = null;
      else catalogCache = rows;
      return rows;
    });
  }
  return catalogPromise;
}

/**
 * An axis-spec lookup over whatever is cached **right now**.
 *
 * Deliberately a live closure rather than a snapshot: the chart builder is
 * constructed before the catalog can have arrived, so a snapshot taken at
 * construction would be empty forever. Reading through on each call means the
 * first re-plan after the fetch resolves picks the levels up — which is what
 * `refreshReferenceLines` is for.
 */
export function cachedAxisSpecs(): (indicatorRef: string) => IndicatorAxisSpec | undefined {
  return (ref) => catalogCache.find((entry) => entry.name === ref)?.axis;
}

/**
 * An event-kind lookup over whatever is cached right now — `cachedAxisSpecs`'
 * live closure, for its reason.
 *
 * Keyed by family *and* alias because the kind is per output: the family is
 * `Plot.indicatorRef`, and the output is the alias' name before its parameters
 * (`outputAlias`'s `<output>_<p1>_…`), so a parameterised family still finds it.
 */
export function cachedEventKinds(): (
  indicatorRef: string,
  outputAlias: string,
) => "single" | "dual" | "flag" | undefined {
  return (ref, alias) =>
    catalogCache
      .find((entry) => entry.name === ref)
      ?.outputs.find((output) => alias === output.name || alias.startsWith(`${output.name}_`))
      ?.eventKind;
}

/**
 * Whatever is cached **right now**, for a caller that only groups.
 *
 * Empty until the first `loadIndicatorCatalog` resolves, and that is a usable
 * answer rather than a wait: `appliedIndicators` falls back to keying by alias
 * with no catalog, which splits a family into one row per output. The legend's
 * per-plot rows are right either way; only the family controls are missing for
 * the moment before it lands, and they reappear on the next render.
 */
export function cachedCatalog(): readonly IndicatorEntry[] {
  return catalogCache;
}

export async function fetchIndicatorCatalog(): Promise<IndicatorEntry[]> {
  if (!IND_URL) return [];
  try {
    // **With the token.** Without one the server answers as nobody, and until
    // 2026-10-12 nobody was permissive: every account saw JAS, JP and PD
    // listed unlocked.
    const response = await fetch(`${IND_URL}/ind/catalog`, { headers: await requestHeaders() });
    if (!response.ok) return [];
    const body = (await response.json()) as { indicators?: IndicatorEntry[] };
    return body.indicators ?? [];
  } catch {
    return [];
  }
}
